import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Printer, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { formatApiError } from "@/lib/api";
import { invoicesAPI, downloadPdf, openPdf, fmtINR } from "@/lib/documents";
import { BTN_PRIMARY, BTN_SOFT, BTN_SECONDARY } from "@/components/checkout/buttonStyles";
import { GSTInvoiceFields, gstFieldsError } from "@/components/documents/GSTInvoiceFields";

const today = () => new Date().toISOString().slice(0, 10);
const Row = ({ label, value, strong, tone, testId }) => (
  <div className={`flex justify-between gap-3 ${strong ? "font-bold text-[14px] pt-2 mt-1 border-t border-[#D5E6E8]" : "text-[12.5px]"}`} data-testid={testId}>
    <span className="text-[#3F5257]">{label}</span><span className={`mono ${tone || "text-[#1A2425]"}`}>{value}</span>
  </div>
);

/** Generate Invoice modal — period · GST toggle · live charge preview · Generate & Download / Print. Bottom sheet on phones. */
export function GenerateInvoiceModal({ open, onClose, stay, gst = false, onDone }) {
  const stayId = stay?.id || stay?._id;
  const checkedOut = stay?.status === "checked_out";
  const [form, setForm] = useState({ period_from: "", period_to: "", is_gst_invoice: gst, gst_company_name: "", gst_number: "", gst_company_address: "", notes: "" });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm({ period_from: (stay?.actual_arrival || stay?.expected_arrival || "").slice(0, 10), period_to: checkedOut ? (stay?.actual_departure || stay?.expected_departure || "").slice(0, 10) : today(),
      is_gst_invoice: gst, gst_company_name: "", gst_number: "", gst_company_address: "", notes: "" });
  }, [open, gst, stay, checkedOut]);

  useEffect(() => {
    if (!open || !stayId || !form.period_from) return;
    const t = setTimeout(() => {
      setPreview(null);
      invoicesAPI.preview({ stay_id: stayId, period_from: form.period_from, period_to: form.period_to || null }).then(setPreview).catch((e) => toast.error(formatApiError(e)));
    }, 300);
    return () => clearTimeout(t);
  }, [open, stayId, form.period_from, form.period_to]);

  const generate = async (mode) => {
    const err = gstFieldsError(form);
    if (err) { toast.error(err); return; }
    setBusy(mode);
    try {
      const inv = await invoicesAPI.create({ stay_id: stayId, period_from: form.period_from || null, period_to: form.period_to || null, is_gst_invoice: form.is_gst_invoice,
        gst_company_name: form.is_gst_invoice ? form.gst_company_name.trim() : null, gst_number: form.is_gst_invoice ? form.gst_number.trim().toUpperCase() : null,
        gst_company_address: form.is_gst_invoice ? form.gst_company_address.trim() : null, notes: form.notes || null });
      toast.success(`${inv.is_gst_invoice ? "GST Tax Invoice" : "Invoice"} ${inv.invoice_number} generated`);
      const url = invoicesAPI.pdfUrl(inv.id);
      const name = `${inv.is_gst_invoice ? "TaxInvoice" : "Invoice"}-${inv.invoice_number}.pdf`;
      if (mode === "download") await downloadPdf(url, name).catch(() => {}); else openPdf(url, name);
      onDone?.(inv); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const cls = "h-10 text-[13px] bg-white border-[#D5E6E8]";
  const gt = preview?.group_totals || {};
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border border-[#D5E6E8] p-0 gap-0 md:max-w-[560px] max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:translate-y-0 max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:max-h-[92vh] overflow-y-auto" data-hocp-dialog data-testid="generate-invoice-modal">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-[#EEF4F5]">
          <DialogTitle className="font-heading text-[16px] text-[#1A2425]">Generate {form.is_gst_invoice ? "GST Tax Invoice" : "Invoice"}{stay?.room_number ? ` — Room ${stay.room_number}` : ""}</DialogTitle>
          <DialogDescription className="text-[12px] text-[#3F5257]">Consolidated invoice covering room charges and all services for the selected period.</DialogDescription>
        </DialogHeader>
        <div className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-[11px] font-semibold text-[#3F5257] uppercase tracking-wide">From<Input type="date" value={form.period_from} onChange={(e) => set("period_from", e.target.value)} className={`${cls} mt-1`} data-testid="invoice-period-from" /></label>
            <label className="text-[11px] font-semibold text-[#3F5257] uppercase tracking-wide">To<Input type="date" value={form.period_to} onChange={(e) => set("period_to", e.target.value)} className={`${cls} mt-1`} data-testid="invoice-period-to" /></label>
          </div>
          <label className="flex items-center gap-2 text-[13px] text-[#1A2425] cursor-pointer">
            <input type="checkbox" checked={form.is_gst_invoice} onChange={(e) => set("is_gst_invoice", e.target.checked)} className="h-4 w-4 accent-[#37F4B5]" data-testid="invoice-gst-toggle" />Generate as GST Tax Invoice
          </label>
          {form.is_gst_invoice && <GSTInvoiceFields values={form} onChange={set} inputClass={cls} testPrefix="invoice-gst" labels={false} />}
          <div className="rounded-lg border border-[#D5E6E8] bg-[#E6FEFF] p-4 space-y-1" data-testid="invoice-preview">
            {!preview ? <div className="flex items-center gap-2 text-[12px] text-[#3F5257]"><Loader2 size={13} className="animate-spin" />Compiling charges…</div> : (<>
              <Row label="Room Charges" value={fmtINR(gt.room)} testId="invoice-preview-room" />
              {gt.fnb > 0 && <Row label="F&B / Services" value={fmtINR(gt.fnb)} />}
              {gt.laundry > 0 && <Row label="Laundry" value={fmtINR(gt.laundry)} />}
              {gt.pool > 0 && <Row label="Pool & Activities" value={fmtINR(gt.pool)} />}
              {gt.misc > 0 && <Row label="Miscellaneous" value={fmtINR(gt.misc)} />}
              {preview.total_adjustments !== 0 && <Row label="Adjustments" value={`−${fmtINR(Math.abs(preview.total_adjustments))}`} tone="text-[#14805E]" />}
              <Row label="Total Tax" value={fmtINR(preview.total_tax)} />
              <Row label="GRAND TOTAL" value={fmtINR(preview.grand_total)} strong testId="invoice-preview-total" />
              <Row label="Total Paid" value={`−${fmtINR(preview.total_paid)}`} />
              <Row label={preview.balance_due < 0 ? "Refund Due" : "Balance Due"} value={<span className="inline-flex items-center gap-1">{fmtINR(Math.abs(preview.balance_due))}{Math.abs(preview.balance_due) < 0.01 && <CheckCircle2 size={13} className="text-[#14C48E]" />}</span>}
                tone={preview.balance_due > 0.009 ? "text-[#B45309] font-bold" : "text-[#14805E] font-bold"} testId="invoice-preview-balance" />
            </>)}
          </div>
          <Input placeholder="Notes (optional)" value={form.notes} onChange={(e) => set("notes", e.target.value)} className={cls} data-testid="invoice-notes" />
        </div>
        <div className="px-5 py-4 border-t border-[#EEF4F5] flex flex-wrap gap-2 max-sm:[&>button]:w-full">
          <button type="button" className={`${BTN_SECONDARY} sm:mr-auto`} onClick={onClose} data-testid="invoice-cancel">Cancel</button>
          <button type="button" className={BTN_SOFT} disabled={!!busy || !preview} onClick={() => generate("print")} data-testid="invoice-generate-print">{busy === "print" ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />}Generate &amp; Print</button>
          <button type="button" className={BTN_PRIMARY} disabled={!!busy || !preview} onClick={() => generate("download")} data-testid="invoice-generate-download">{busy === "download" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}Generate &amp; Download</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
